import { NgStyle } from '@angular/common'
import { Component, DestroyRef, OnInit, inject } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormsModule } from '@angular/forms'
import { FaIconComponent } from '@fortawesome/angular-fontawesome'
import { faX } from '@fortawesome/free-solid-svg-icons'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'
import imageCompression, { Options } from 'browser-image-compression'

import {
    ListItem,
    PendingChangeOrder,
    PendingChangeOrderInitiatedBy,
    PendingChangeOrderPricingMethod,
    PendingChangeOrderWorkType,
    RepositoryModal,
    createPendingChangeOrderInitiatedByListItems,
    createPendingChangeOrderPricingMethodListItems,
    createPendingChangeOrderWorkTypeListItems,
} from '../../shared'
import { DateSetTodayToggleComponent } from '../../shared/reusable-components/date-set-today-toggle/date-set-today-toggle.component'
import { VoiceTextFieldComponent } from '../../shared/components/voice-text-field/voice-text-field.component'
import { PendingChangeOrdersComponent } from './pending-change-orders.component'

@Component({
    templateUrl: './pending-change-order.component.html',
    standalone: true,
    imports: [FormsModule, FaIconComponent, WjInputModule, DateSetTodayToggleComponent, NgStyle, VoiceTextFieldComponent],
})
export class PendingChangeOrderComponent extends RepositoryModal<PendingChangeOrder> implements OnInit {
    faX = faX

    private destroyRef = inject(DestroyRef)
    private parentComponent!: PendingChangeOrdersComponent

    jobOptions: ListItem<string>[] = []
    estimateCodeOptions: ListItem<string>[] = []

    initiatedByOptions = createPendingChangeOrderInitiatedByListItems()

    workTypeOptions = createPendingChangeOrderWorkTypeListItems()

    pricingMethodOptions = createPendingChangeOrderPricingMethodListItems()

    images: any[] = []
    videos: any[] = []
    documents: any[] = []
    progress = 0
    private hadSentToOwnerDate = false

    ngOnInit() {
        this.parentComponent = this.editOptions.initializeParams.component as PendingChangeOrdersComponent

        if (this.isAdding) {
            this.target.createdOn = new Date()
            this.target.dateInitiated = new Date()
            this.target.initiatedBy = PendingChangeOrderInitiatedBy.Owner
            this.target.workType = PendingChangeOrderWorkType.CriticalPath
            this.target.pricingMethod = PendingChangeOrderPricingMethod.TM
        }

        if (this.isDuplicating) {
            this.target.imageUrls = []
        }

        this.target.initiatedBy ??= PendingChangeOrderInitiatedBy.Owner
        this.target.workType ??= PendingChangeOrderWorkType.CriticalPath
        this.target.pricingMethod ??= PendingChangeOrderPricingMethod.TM

        this.jobOptions = this.parentComponent.jobMap
        this.estimateCodeOptions = this.parentComponent.estimateCodes.map(code => new ListItem<string>(code, code))
        this.hadSentToOwnerDate = this.target.sentToOwnerDate != null
    }

    onWorkTypeChange(workType: PendingChangeOrderWorkType) {
        if (workType === PendingChangeOrderWorkType.CriticalPath && this.target.pricingMethod !== PendingChangeOrderPricingMethod.TM) {
            this.target.pricingMethod = PendingChangeOrderPricingMethod.TM
        } else if (workType === PendingChangeOrderWorkType.FutureWork && this.target.pricingMethod !== PendingChangeOrderPricingMethod.LumpSum) {
            this.target.pricingMethod = PendingChangeOrderPricingMethod.LumpSum
        }
    }

    private static readonly programMgrPositionCodes = ['PROGRAM M', 'OPM', 'CEO']
    private static readonly vpPositionCodes = ['OPM', 'CEO']

    get isProgramMgrApprovalDisabled(): boolean {
        const code = this.services.authentication.positionCode()
        return !PendingChangeOrderComponent.programMgrPositionCodes.includes(code)
    }

    get isVpApprovalDisabled(): boolean {
        if (!this.target.approxCost || this.target.approxCost <= 20000) {
            return true
        }
        const code = this.services.authentication.positionCode()
        return !PendingChangeOrderComponent.vpPositionCodes.includes(code)
    }

    get isSentToOwnerRowDisabled(): boolean {
        if (!this.target.programMgrApprovalDate) {
            return true
        }
        if (this.target.approxCost && this.target.approxCost > 20000 && !this.target.vpApprovalDate) {
            // Program Manager approvers (e.g. Doug) may proceed before VP (Chad) approval
            const code = this.services.authentication.positionCode()
            return !PendingChangeOrderComponent.programMgrPositionCodes.includes(code)
        }
        return false
    }

    onSentToOwnerDateChange(value: Date | null) {
        if (value != null && !this.hadSentToOwnerDate) {
            this.hadSentToOwnerDate = true
            this.services.modals.confirm(
                'Document Reminder',
                'Have you attached the most current document(s) for this change order? If not, please attach them before saving.',
            )
        }
        if (value == null) {
            this.hadSentToOwnerDate = false
        }
    }

    removeImage(image) {
        this.images.remove(image)
    }

    removeVideo(video) {
        this.videos.remove(video)
    }

    removeDocument(doc) {
        this.documents.remove(doc)
    }

    getPendingImageSrc(image) {
        if (image.type === 'application/pdf') {
            return './Resources/pdfImage.png'
        }
        return image.url
    }

    private static readonly VIDEO_EXTENSIONS = ['.mp4', '.webm', '.mov', '.avi']

    isVideo(url: string): boolean {
        const lower = url.toLowerCase()
        return PendingChangeOrderComponent.VIDEO_EXTENSIONS.some(ext => lower.endsWith(ext))
    }

    isPdf(url: string): boolean {
        return url.toLowerCase().endsWith('.pdf')
    }

    getExistingFileSrc(url: string) {
        return './Resources/Images/PendingChangeOrder/' + url
    }

    getExistingImageSrc(url: string) {
        if (url.indexOf('.pdf') > -1) {
            return './Resources/pdfImage.png'
        }
        return './Resources/Images/PendingChangeOrder/' + url + '?w=300'
    }

    openImage(url) {
        window.open('./Resources/Images/PendingChangeOrder/' + url, '_blank')
    }

    async onPhotoChanged(event) {
        const files = event.target.files
        const progress = (p: number) => {
            this.progress = p
        }

        for (const file of files) {
            const options: Options = {
                maxSizeMB: 2,
                maxWidthOrHeight: 1920,
                useWebWorker: true,
                onProgress: progress,
            }
            const compressedFile = await imageCompression(file, options)
            compressedFile['url'] = await imageCompression.getDataUrlFromFile(compressedFile)
            this.images.push(compressedFile)
        }
    }

    onVideoChanged(event) {
        const files = event.target.files
        for (const file of files) {
            this.videos.push(file)
        }
    }

    onDocumentChanged(event) {
        const files = event.target.files
        for (const file of files) {
            this.documents.push(file)
        }
    }

    private get allFiles(): any[] {
        return [...this.images, ...this.videos, ...this.documents]
    }

    protected onSave(resolveSave: (result?: any) => void, rejectSave: (reason?: any) => void) {
        const observable = this.isAdding || this.isDuplicating ? this.repositoryAdd() : this.repositoryEdit()
        observable.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: successResult => {
                if (this.allFiles.length === 0) {
                    resolveSave(successResult)
                    return
                }

                const uploadData = new FormData()
                uploadData.append('id', successResult.id)
                const files = this.allFiles
                for (let i = 0; i < files.length; i++) {
                    uploadData.append('myFile' + i, files[i], files[i].name)
                }
                this.services.data
                    .post('api/PendingChangeOrder/UploadImages', uploadData)
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe({
                        next: () => {
                            resolveSave(successResult)
                        },
                        error: errorResult => {
                            rejectSave(errorResult)
                        },
                    })
            },
            error: errorResult => {
                rejectSave(errorResult)
            },
        })
    }

    protected onBeforeSave() {
        if (!this.target.createdOn) {
            this.target.createdOn = new Date()
        }
        if (!this.target.dateInitiated) {
            this.target.dateInitiated = new Date()
        }
        this.target.initiatedBy ??= PendingChangeOrderInitiatedBy.Owner
        this.target.workType ??= PendingChangeOrderWorkType.CriticalPath
        this.target.pricingMethod ??= PendingChangeOrderPricingMethod.TM
        return true
    }
}
